import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import API_BASE_URL from "../config";
import useJobs from "../hooks/useJobs";

// Single source of truth for the background-jobs activity surface.
//
// Primary transport is a Server-Sent Events feed (`/api/v1/jobs/stream`): the server
// runs ONE shared poll loop and pushes the jobs snapshot to every connected client,
// so each browser holds a single idle connection instead of polling — server load is
// independent of the number of viewers. If SSE can't be established (unsupported, or
// blocked by a proxy / backend 503), we fall back to the previous polling hook.
const JobsContext = createContext(null);

// Consecutive transient reconnects tolerated before giving up on SSE for this session.
const MAX_SSE_ERRORS = 4;

// If no message (snapshot OR heartbeat) arrives for this long, the connection is
// treated as silently stalled (e.g. dropped by a network layer without a clean
// close/error — EventSource may never fire onerror in that case) and we fall back
// to polling. Comfortably more than 2x the backend's default 30s heartbeat so a
// single missed beat + jitter doesn't false-trigger.
const SSE_STALE_MS =
  Number(
    (window._env_ && window._env_.REACT_APP_JOBS_SSE_STALE_MS) ||
      process.env.REACT_APP_JOBS_SSE_STALE_MS,
  ) || 90000;
// How often to check for staleness — independent of and shorter than SSE_STALE_MS.
const SSE_STALE_CHECK_MS = 15000;

export function JobsProvider({ children }) {
  const supportsSSE = typeof window !== "undefined" && "EventSource" in window;
  const [usePolling, setUsePolling] = useState(!supportsSSE);
  const [streamJobs, setStreamJobs] = useState([]);
  const [streamAvailable, setStreamAvailable] = useState(true);

  // Fallback poller — its interval runs ONLY while usePolling is true (enabled gate).
  // startJob/refresh are always available (stable useCallbacks) regardless of mode.
  const polled = useJobs({ enabled: usePolling });
  const { startJob, refresh } = polled;

  const esRef = useRef(null);
  const errorsRef = useRef(0);
  const lastMessageAtRef = useRef(Date.now());

  useEffect(() => {
    if (usePolling || !supportsSSE) return undefined;
    const es = new EventSource(`${API_BASE_URL}/api/v1/jobs/stream`, { withCredentials: true });
    esRef.current = es;
    lastMessageAtRef.current = Date.now(); // baseline for this connection attempt

    es.onmessage = (e) => {
      lastMessageAtRef.current = Date.now(); // any data at all proves the connection is alive
      let payload;
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }
      errorsRef.current = 0; // any received message proves the connection is healthy
      if (payload.type === "snapshot") {
        setStreamJobs(Array.isArray(payload.jobs) ? payload.jobs : []);
        setStreamAvailable(true);
      }
      // payload.type === "heartbeat" → keep-alive, ignore (no re-render)
    };

    es.onerror = () => {
      // EventSource auto-reconnects on transient drops (readyState CONNECTING). Only
      // give up → polling on a hard failure (CLOSED: e.g. 503 / proxy strips SSE) or
      // after several consecutive transient failures with no successful message.
      errorsRef.current += 1;
      const hardFail = es.readyState === EventSource.CLOSED;
      if (hardFail || errorsRef.current >= MAX_SSE_ERRORS) {
        es.close();
        esRef.current = null;
        setUsePolling(true); // permanent fallback for this session (avoids flapping)
      }
    };

    // Watchdog: a connection dropped by a network layer without a clean close never
    // fires onerror, so onerror alone can't be trusted to detect every failure mode.
    // If neither a snapshot nor a heartbeat has arrived in SSE_STALE_MS, treat it the
    // same as a hard failure.
    const staleCheck = setInterval(() => {
      if (Date.now() - lastMessageAtRef.current > SSE_STALE_MS) {
        es.close();
        esRef.current = null;
        setUsePolling(true);
      }
    }, SSE_STALE_CHECK_MS);

    return () => {
      clearInterval(staleCheck);
      es.close();
      esRef.current = null;
    };
  }, [usePolling, supportsSSE]);

  const value = useMemo(
    () =>
      usePolling
        ? { jobs: polled.jobs, available: polled.available, startJob, refresh }
        : { jobs: streamJobs, available: streamAvailable, startJob, refresh },
    [usePolling, polled.jobs, polled.available, streamJobs, streamAvailable, startJob, refresh],
  );

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

/** Read the shared jobs state ({ jobs, available, startJob, refresh }). */
export function useJobsContext() {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error("useJobsContext must be used within a JobsProvider");
  return ctx;
}

export default JobsProvider;
